/**
 * THE SEASON ENGINE ROSTER (Chris, 2026-09-11) — which engine works which turn
 * on which date, decided over the WHOLE season, not day by day in isolation.
 *
 * Chris's rulings:
 *   1. "For minimum contract engines you need to intelligently spread the
 *      mileage consumption over the season, not just consume it in one gigantic
 *      block… be aware of how long it will be in steam so it's run in logical
 *      consecutive time blocks. You will interleave other engines into that
 *      ranking model."
 *   2. "Whenever you have no engines available due to lack of contract miles,
 *      substitute the cheapest NYMR owned steam as default, if that isn't
 *      available then the cheapest contract steam and then finally diesel."
 *   3. "If there is genuinely no fleet then it's a non-running day — mark it in
 *      RED as a failure day."
 *   And the planner may SWAP an engine by hand on the modelling page: the chosen
 *   engine takes that turn from that month on, for as long as it is available.
 *
 * How a steam turn is engined on a date (in this order):
 *   0. THE PLANNER'S PIN for this turn, if that engine is available today and
 *      may take the turn (Whitby, Firecon).
 *   A. PAID-FOR MILES FIRST. Among the available steam engines with a contracted
 *      minimum not yet used up: the engine that holds the turn KEEPS it for a
 *      block of BLOCK_DAYS running days (while it is available and has paid-for
 *      miles left); at the end of a block the turn goes to the engine most
 *      BEHIND its spread — its minimum laid evenly over the running days it is
 *      available this season. So an engine runs in spells of a week or more and
 *      the minimum-contract engines interleave through the season instead of one
 *      burning its miles in a lump; a washout week ends a spell naturally.
 *   B. The cheapest NYMR-owned steam (no hire), in the register's order.
 *   C. The cheapest contract steam for the day (£/mile × miles + day rate) — an
 *      engine past its minimum, or one with no minimum.
 *   D. The cheapest diesel for the day — the turn RUNS DIESEL, marked as swapped.
 *   Nothing → the turn is uncovered and the DATE IS A FAILURE DAY: the timetable
 *   cannot run as designed, so the day is valued as a non-running day and marked
 *   red wherever dates are shown. A diesel or railcar turn takes the same
 *   ranking within its own traction (paid-for minimums first, then cheapest).
 *   Whitby turns are engined first each day and only by Whitby-enabled engines;
 *   on a Firecon day only an oil-fired engine may keep steam.
 *
 * One owner: the roster is built once per season (ResearchStudio::seasonRoster)
 * from the banked patterns, and the per-date valuation, the modelling page's
 * engine labels, the fleet planner, the board paper and the workbook all read
 * it. costs.ts::costFromDeps applies a date's assignments through applyRoster.
 */
import type { Departure, FleetLoco, Network, Traction } from './types'
import { designateDiagrams, diagramTouchesWhitby, type DesignatedDiagram } from './diagrams'
import { journeyMiles } from './network'
import { engineOutOn } from './fleetAvailability'
import { engineLabel } from './fleetRegister'
import { fmtMin } from './demand'
import type { FireconDay } from './firecon'

/** Days an engine keeps a turn before the spread is allowed to hand it on. */
export const BLOCK_DAYS = 7

export interface RosterTurn { key: string; traction: Traction; miles: number; whitby: boolean; firstDepMin: number; legIds: string[] }

/** The stable identity of a turn within a colour's pattern: colour | first departure | from | to. */
export function turnKeyOf(net: Network, colour: string, first: Departure): string {
  return `${colour.toUpperCase()}|${fmtMin(first.minutes)}|${net.stations[first.o]?.code ?? first.o}|${net.stations[first.d]?.code ?? first.d}`
}

/** A pattern's turns — its engine diagrams as diagrams.ts chains them — with their TIMETABLED miles. */
export function patternTurns(net: Network, colour: string, deps: Departure[], dl: number): RosterTurn[] {
  const enabled = deps.filter((d) => d.enabled && Number.isFinite(d.minutes))
  const byId = new Map(enabled.map((d) => [d.id, d]))
  return designateDiagrams(net, enabled, dl).flatMap(({ diagram, traction }) => {
    const first = byId.get(diagram.legIds[0])
    if (!first) return []
    let miles = 0
    for (const id of diagram.legIds) { const d = byId.get(id); if (d && !d.positioning) miles += journeyMiles(net, d.o, d.d) }
    return [{ key: turnKeyOf(net, colour, first), traction, miles, whitby: diagramTouchesWhitby(net, diagram, byId), firstDepMin: diagram.firstDepMin, legIds: diagram.legIds.slice() }]
  })
}

export interface EnginePin { turnKey: string; engineId: number; fromDate: string }
export interface RosterDate { date: string; month: number; colour: string; turns: RosterTurn[]; firecon?: FireconDay | null }
export interface RosterAssignment { key: string; traction: Traction; engineId: number | null; swappedToDiesel: boolean; pinned: boolean; reason: string; miles: number }
export interface RosterDay { date: string; colour: string; turns: RosterAssignment[]; failure: boolean; uncovered: string[] }
export interface EngineSeason {
  engineId: number; label: string; traction: Traction; minimum: number | null
  miles: number; days: number; milesByMonth: Record<number, number>; daysByMonth: Record<number, number>
  /** How many separate runs of consecutive days the engine worked (fewer = longer blocks). */
  blocks: number
}
export interface Roster {
  byDate: Map<string, RosterDay>
  engines: Record<string, EngineSeason>
  failureDays: number
  swappedTurns: number
  pinnedTurns: number
}

const dayCost = (l: FleetLoco, miles: number): number => (l.costPerMile ?? 0) * miles + (l.dayRate ?? 0)
const hasMinimum = (l: FleetLoco): boolean => (l.minContractMiles ?? 0) > 0
const isOwnedFree = (l: FleetLoco): boolean => l.ownership === 'nymr' && !(l.costPerMile ?? 0) && !(l.dayRate ?? 0)
const byOrder = (a: FleetLoco, b: FleetLoco) => (a.sortOrder - b.sortOrder) || (a.id - b.id)

export function buildRoster(dates: RosterDate[], locos: FleetLoco[], pins: EnginePin[] = []): Roster {
  const use = locos.filter((l) => l.inSimulation)
  const sorted = [...dates].sort((a, b) => a.date.localeCompare(b.date))
  // Each engine's available running days this season → its spread target.
  const availDates = new Map<number, string[]>()
  for (const l of use) availDates.set(l.id, sorted.filter((d) => !engineOutOn(l, d.date)).map((d) => d.date))
  const used: Record<number, number> = {}
  const days: Record<number, number> = {}
  const milesByMonth: Record<number, Record<number, number>> = {}
  const daysByMonth: Record<number, Record<number, number>> = {}
  const blocks: Record<number, number> = {}
  const lastEngineOnTurn = new Map<string, number>()
  /** Who holds each turn and for how many running days so far — the block. */
  const incumbent = new Map<string, { engineId: number; held: number }>()
  const target = (l: FleetLoco, date: string): number => {
    const min = l.minContractMiles ?? 0
    const av = availDates.get(l.id) ?? []
    if (min <= 0 || av.length === 0) return 0
    const i = av.indexOf(date)
    const n = i >= 0 ? i + 1 : av.filter((x) => x <= date).length
    return min * (n / av.length)
  }
  const byDate = new Map<string, RosterDay>()
  let failureDays = 0, swappedTurns = 0, pinnedTurns = 0

  for (const d of sorted) {
    const available = use.filter((l) => !engineOutOn(l, d.date))
    const taken = new Set<number>()
    const mayTake = (l: FleetLoco, t: RosterTurn): boolean => !taken.has(l.id) && (!t.whitby || l.whitbyEnabled) && (!d.firecon || l.traction !== 'steam' || l.fuel === 'oil')
    const pinFor = (t: RosterTurn): EnginePin | undefined => pins.filter((p) => p.turnKey === t.key && p.fromDate <= d.date).sort((a, b) => b.fromDate.localeCompare(a.fromDate))[0]
    const pinUsable = (t: RosterTurn): boolean => { const p = pinFor(t); const l = p && available.find((x) => x.id === p.engineId); return !!l && mayTake(l, t) }
    // The planner's pinned turns first (their engine must not be taken by another
    // turn), then Whitby turns (they need the cleared engines), steam before
    // diesel, then by time.
    const order = [...d.turns].sort((a, b) => (Number(pinUsable(b)) - Number(pinUsable(a))) || (Number(b.whitby) - Number(a.whitby))
      || ((a.traction === 'steam' ? 0 : 1) - (b.traction === 'steam' ? 0 : 1)) || (a.firstDepMin - b.firstDepMin))
    const pool = (tr: Traction, t: RosterTurn) => available.filter((l) => l.traction === tr && mayTake(l, t))
    /** A: paid-for miles first — the holder keeps the turn for a block, then the most-behind takes it. */
    const paidFor = (cands: FleetLoco[], t: RosterTurn): { l: FleetLoco; reason: string } | null => {
      const a = cands.filter((l) => hasMinimum(l) && (used[l.id] ?? 0) < (l.minContractMiles ?? 0))
      if (a.length === 0) return null
      const inc = incumbent.get(t.key)
      const holder = inc && inc.held < BLOCK_DAYS ? a.find((l) => l.id === inc.engineId) : undefined
      if (holder) return { l: holder, reason: `contracted miles — ${Math.round((used[holder.id] ?? 0) + t.miles).toLocaleString('en-GB')} of ${(holder.minContractMiles ?? 0).toLocaleString('en-GB')} after today · day ${inc!.held + 1} of its block` }
      const behind = (l: FleetLoco): number => target(l, d.date) - (used[l.id] ?? 0)
      const best = a.slice().sort((x, y) => (behind(y) - behind(x)) || byOrder(x, y))[0]
      return { l: best, reason: `contracted miles — ${Math.round((used[best.id] ?? 0) + t.miles).toLocaleString('en-GB')} of ${(best.minContractMiles ?? 0).toLocaleString('en-GB')} after today · most behind its spread` }
    }
    const cheapest = (cands: FleetLoco[], t: RosterTurn): FleetLoco | null =>
      cands.slice().sort((x, y) => (dayCost(x, t.miles) - dayCost(y, t.miles)) || byOrder(x, y))[0] ?? null

    const out: RosterAssignment[] = []
    const dayMiles: [number, number][] = []
    for (const t of order) {
      let chosen: { l: FleetLoco; reason: string; pinned?: boolean } | null = null
      // 0. the planner's pin
      const pin = pinFor(t)
      if (pin) {
        const l = available.find((x) => x.id === pin.engineId)
        if (l && mayTake(l, t)) chosen = { l, reason: `pinned by the planner from ${pin.fromDate}`, pinned: true }
      }
      if (!chosen && t.traction === 'steam') {
        const steam = pool('steam', t)
        chosen = paidFor(steam, t)
        if (!chosen) { const b = steam.filter(isOwnedFree).sort(byOrder)[0]; if (b) chosen = { l: b, reason: 'owned — no hire' } }
        if (!chosen) { const c = cheapest(steam, t); if (c) chosen = { l: c, reason: `cheapest contract steam — £${Math.round(dayCost(c, t.miles)).toLocaleString('en-GB')} for the day` } }
        if (!chosen) { const dz = cheapest(pool('diesel', t), t); if (dz) chosen = { l: dz, reason: d.firecon ? 'Firecon — steam stood down, diesel' : `no steam engine available — diesel, £${Math.round(dayCost(dz, t.miles)).toLocaleString('en-GB')} for the day` } }
      } else if (!chosen) {
        const cands = pool(t.traction, t)
        chosen = paidFor(cands, t)
        if (!chosen) { const c = cheapest(cands, t); if (c) chosen = { l: c, reason: isOwnedFree(c) ? 'owned — no hire' : `cheapest ${t.traction} — £${Math.round(dayCost(c, t.miles)).toLocaleString('en-GB')} for the day` } }
      }
      if (chosen) {
        taken.add(chosen.l.id)
        dayMiles.push([chosen.l.id, t.miles])
        const swapped = t.traction === 'steam' && chosen.l.traction === 'diesel'
        out.push({ key: t.key, traction: chosen.l.traction, engineId: chosen.l.id, swappedToDiesel: swapped, pinned: !!chosen.pinned, reason: chosen.reason, miles: t.miles })
      } else {
        out.push({ key: t.key, traction: t.traction, engineId: null, swappedToDiesel: false, pinned: false, reason: 'no engine of any traction is available — a failure day', miles: t.miles })
      }
    }
    const uncovered = out.filter((x) => x.engineId == null).map((x) => x.key)
    const failure = d.turns.length > 0 && uncovered.length > 0
    if (failure) {
      // The day does not run: no engine works, no miles are used, the pins stand.
      failureDays += 1
    } else {
      for (const a of out) {
        if (a.engineId == null) continue
        if (a.swappedToDiesel) swappedTurns += 1
        if (a.pinned) pinnedTurns += 1
        if (lastEngineOnTurn.get(a.key) !== a.engineId) blocks[a.engineId] = (blocks[a.engineId] ?? 0) + 1
        lastEngineOnTurn.set(a.key, a.engineId)
        const inc = incumbent.get(a.key)
        incumbent.set(a.key, inc && inc.engineId === a.engineId ? { engineId: a.engineId, held: inc.held + 1 } : { engineId: a.engineId, held: 1 })
      }
      for (const [id, miles] of dayMiles) {
        used[id] = (used[id] ?? 0) + miles
        days[id] = (days[id] ?? 0) + 1
        milesByMonth[id] = milesByMonth[id] ?? {}; milesByMonth[id][d.month] = (milesByMonth[id][d.month] ?? 0) + miles
        daysByMonth[id] = daysByMonth[id] ?? {}; daysByMonth[id][d.month] = (daysByMonth[id][d.month] ?? 0) + 1
      }
    }
    byDate.set(d.date, { date: d.date, colour: d.colour, turns: out, failure, uncovered })
  }
  const engines: Record<string, EngineSeason> = {}
  for (const l of use) {
    engines[String(l.id)] = {
      engineId: l.id, label: engineLabel(l), traction: l.traction, minimum: hasMinimum(l) ? l.minContractMiles! : null,
      miles: Math.round((used[l.id] ?? 0) * 10) / 10, days: days[l.id] ?? 0,
      milesByMonth: milesByMonth[l.id] ?? {}, daysByMonth: daysByMonth[l.id] ?? {}, blocks: blocks[l.id] ?? 0,
    }
  }
  return { byDate, engines, failureDays, swappedTurns, pinnedTurns }
}

/**
 * A date's roster laid over a day's designated diagrams (costs.ts::costFromDeps):
 * each diagram takes the engine the roster gave its turn; a turn the roster left
 * without an engine stays unengined (and is reported as 'no-engine').
 */
export function applyRoster(net: Network, colour: string, designated: { diagram: DesignatedDiagram['diagram']; traction: Traction }[], byId: Map<string, Departure>, day: RosterDay, locos: FleetLoco[]): DesignatedDiagram[] {
  const byKey = new Map(day.turns.map((t) => [t.key, t]))
  return designated.map(({ diagram, traction }) => {
    const first = byId.get(diagram.legIds[0])
    const a = first ? byKey.get(turnKeyOf(net, colour, first)) : undefined
    diagram.engineKey = undefined
    if (!a) return { diagram, traction }
    const engine = a.engineId != null ? locos.find((l) => l.id === a.engineId) : undefined
    if (engine) diagram.engineKey = String(engine.id)
    return { diagram, traction: engine ? engine.traction : a.traction, engine, ...(a.swappedToDiesel ? { swappedFromSteam: true } : {}) }
  })
}

/** "92134 (1–13 Jul), 44806 (14–20 Jul)" — an engine's runs over a list of dated assignments. */
export function describeRuns(rows: { date: string; label: string }[]): string {
  const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const dm = (d: string) => `${Number(d.slice(8, 10))} ${MON[Number(d.slice(5, 7)) - 1]}`
  const runs: { label: string; from: string; to: string }[] = []
  for (const r of rows) {
    const last = runs[runs.length - 1]
    if (last && last.label === r.label) last.to = r.date
    else runs.push({ label: r.label, from: r.date, to: r.date })
  }
  return runs.map((r) => `${r.label} (${r.from === r.to ? dm(r.from) : `${dm(r.from)}–${dm(r.to)}`})`).join(', ')
}

export interface RosterLabel { code: string; label: string; traction: Traction; detail: string; pinned: boolean; engineId: number | null }
/**
 * The engine label for each turn of a pattern over a month's dates — the engine
 * that works it most days, "+n" when others share it, with the runs in detail
 * (for the modelling page's working-turn chips).
 */
export function rosterLabelsFor(roster: Roster, dates: string[], turns: RosterTurn[], locos: FleetLoco[]): Record<string, RosterLabel> {
  const out: Record<string, RosterLabel> = {}
  for (const t of turns) {
    const rows: { date: string; label: string; engineId: number | null; traction: Traction; pinned: boolean }[] = []
    for (const dt of dates) {
      const a = roster.byDate.get(dt)?.turns.find((x) => x.key === t.key)
      if (!a) continue
      const l = a.engineId != null ? locos.find((x) => x.id === a.engineId) : undefined
      rows.push({ date: dt, label: l ? engineLabel(l) : 'no engine', engineId: a.engineId, traction: a.traction, pinned: a.pinned })
    }
    if (rows.length === 0) continue
    const count = new Map<string, number>()
    for (const r of rows) count.set(r.label, (count.get(r.label) ?? 0) + 1)
    const [majority, n] = [...count.entries()].sort((a, b) => b[1] - a[1])[0]
    const others = count.size - 1
    const maj = rows.find((r) => r.label === majority)!
    out[t.key] = {
      code: majority, label: others > 0 ? `${majority} +${others}` : majority, traction: maj.traction, engineId: maj.engineId,
      pinned: rows.some((r) => r.pinned),
      detail: `${n} of ${rows.length} days ${majority}${others > 0 ? ` · ${describeRuns(rows)}` : ''}`,
    }
  }
  return out
}
